import {
  HELLOTECH_HOME_PLAN_PRODUCT_ID,
  HELLOTECH_LITE_PLAN,
  HELLOTECH_LITE_PLAN_ANNUAL_ID,
  HELLOTECH_LITE_PLAN_PRODUCT_ID,
  MONTHLY_PLAN_ID,
  ONLINE_PLAN_PRODUCT_ID,
  PRICING_TEST_MONTHLY_ID,
  PRICING_TEST_YEARLY_ID,
  UPSELL_PLAN_BILLING_CYCLES,
  UPSELL_PLAN_ID,
} from '@constants/plan';
import { HELLOTECH_LITE_DETAILS, ONLINE_DETAILS, SMART_HOME_BENEFITS_DETAILS } from '@constants/planBenefits';
import { SPLITIONAME_PLAN_PRICING } from '@integrations/split/split.constants';
import { roundToNearestTen } from '@utils/formatting';
import { HIDE_PRODUCT_CONNECTED_HOME } from '@constants/base';

/* Hooks */
import useSplit from '@integrations/split/useSplit';

/* Types */
import { TClientPlans, TPlans, TPlansInfo, TPlansInfoFeatured } from 'types/plans.types';

type FilterPlanProductsProps = {
  planProducts: TClientPlans[];
  isPlanPricingTest?: boolean;
};

type FilterBenefitsProps = {
  isMobileOrTablet: boolean;
  plan: TPlans;
};

export type FormatPlansForReducer = {
  planProducts: TClientPlans[];
  featured: boolean;
  isMobileOrTablet: boolean;
  isPlanPricingTest?: boolean;
};

/*
  Api call for active subscriptions data
*/
// @ts-ignore
export const getSubscriptionPlansInfo = ({ api }) =>
  new Promise((resolve, reject) => {
    api.plans.subscriptions
      .all()
      // @ts-ignore
      .then(({ err, data }) => {
        if (err) {
          reject({ err, data });
        } else {
          resolve({ data });
        }
      })
      .catch(reject);
  });

/**
 * Get Upsell Plan Ids
 */
export const getUpsellPlanIds = ({ isPlanPricingTest = true }) => {
  if (isPlanPricingTest) {
    return {
      yearlyID: PRICING_TEST_YEARLY_ID,
      monthlyID: PRICING_TEST_MONTHLY_ID,
    };
  }
  return {
    yearlyID: UPSELL_PLAN_ID,
    monthlyID: MONTHLY_PLAN_ID,
  };
};

/*
  Filter a list of planProducts to get Smart Home Plus plans.
  Return the yearly and monthly SHP plans.

  If `isPlanPricingTest` is set to true, then return the test set of SHP plans.
     NOTE: isPlanPricingTest is permanent, removed the split. True always.
*/
const filterPlanProducts = ({ planProducts = [] }: FilterPlanProductsProps) => {
  if (!Array.isArray(planProducts) || !planProducts.length) return null;

  const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const smartHomePlanProduct = planProducts.find(product => product.id === HELLOTECH_HOME_PLAN_PRODUCT_ID);

  if (smartHomePlanProduct) {
    const { yearlyID, monthlyID } = getUpsellPlanIds({});

    const smartHomePlans = smartHomePlanProduct.prices.reduce(
      (accu, current): $FixMe => {
        const { id, planInterval } = current;
        if (id === yearlyID || id === monthlyID) {
          return { ...accu, [planInterval]: current };
        }
        return accu;
      },
      { [ANNUAL]: null, [MONTHLY]: null },
    );

    return smartHomePlans;
  }

  return null;
};

/*
  Given a single subscription plan, let's accurately show the benefits
  based on the plan's included_skus.
*/
export const filterBenefits = ({ plan }: FilterBenefitsProps) => {
  const { includedSkus = [], truckRollFee, productDiscount: discount } = (plan || {}) as TPlans;
  const { inHome, online, checkup, antivirus, password, productDiscount } = SMART_HOME_BENEFITS_DETAILS;
  const benefits = [];
  const { amount: discountAmount } = discount || {};

  benefits.push(online);
  benefits.push({ ...inHome(truckRollFee), truckRollFee });

  if (!HIDE_PRODUCT_CONNECTED_HOME && typeof productDiscount === 'function') {
    benefits.push({ ...productDiscount(discountAmount), discountAmount });
  }

  includedSkus.forEach(sku => {
    const skuName = sku.name.toLowerCase();

    if (skuName.includes('checkup')) {
      benefits.push(checkup);
    } else if (skuName.includes('malwarebytes')) {
      benefits.push(antivirus);
    } else if (skuName.includes('dashlane')) {
      benefits.push(password);
    }
  });

  return benefits;
};

/*
  Given a list of plan products, get the smart home plan product and filter it
  for the yearly and monthly subscription plans. With those yearly/monthly
  plans, format in the desired shape for usage.

  The return value
  {
    yearly: {
      id: 1,
      name: 'Smart Home Plus',
      amount: 149,
      benefits: [],
      ...,
    },
    monthly: {
      id: 5,
      name: 'Smart Home Plus',
      amount: 14.99,
      benefits: [],
      ...,
    },
    featured: true
  }
*/
export const formatPlansForReducer = ({ planProducts, featured = true, isMobileOrTablet = false }: FormatPlansForReducer) => {
  const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const smartHomePlans = filterPlanProducts({ planProducts }) as {};
  let formatted = {};
  [ANNUAL, MONTHLY].forEach(cycle => {
    formatted = {
      ...formatted,
      [cycle]: {
        ...(smartHomePlans as Record<string, any>)[cycle],
        benefits: filterBenefits({ plan: (smartHomePlans as Record<string, any>)[cycle], isMobileOrTablet }),
      },
    };
  });

  return { ...formatted, featured } as TPlansInfoFeatured;
};

/*
  Get old and new prices of upsell plan (yearly and monthly) from splitconfig
  This is used in ServiceInfoUpsell, membership page, and upsell cart components
*/
export const getAnnualAndMonthlySplitPlanPricing = (
  isMonthly: boolean,
): {
  oldPrice: string;
  currentPrice: string;
  planPricingSplit: boolean;
} => {
  const {
    isLoaded: planPricingSplitLoaded,
    treatment: planPricingSplitTreatment,
    treatmentConfig: planPricingSplitConfig,
  } = useSplit(SPLITIONAME_PLAN_PRICING);

  const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const planPricingSplit = planPricingSplitTreatment === 'showSavings';
  const { oldPrice: oldPriceAnnual, currentPrice: currentPriceAnnual } = (planPricingSplit && planPricingSplitConfig?.[ANNUAL]) || {};
  const { oldPrice: oldPriceMonthly, currentPrice: currentPriceMonthly } = (planPricingSplit && planPricingSplitConfig?.[MONTHLY]) || {};
  const prices = isMonthly
    ? { oldPrice: oldPriceMonthly, currentPrice: currentPriceMonthly, planPricingSplit }
    : { oldPrice: oldPriceAnnual, currentPrice: currentPriceAnnual, planPricingSplit };

  return planPricingSplitLoaded
    ? prices
    : {
        oldPrice: '',
        currentPrice: '',
        planPricingSplit: false,
      };
};

export const getPlansReviews = ({ planProducts }: FilterPlanProductsProps) => {
  const [latestReviews = []] = planProducts.map(product => product.latestReviews);
  return latestReviews;
};

/*
  Get a rough estimate of savings between yearly and monthly hellotech Home plans
*/
export const getAnnualSavings = (hellotechHomePlans: TPlansInfo) => {
  if (!Object.keys(hellotechHomePlans).length) return null;
  const { yearly, monthly } = hellotechHomePlans;
  const { amount: yearlyAmount } = yearly || {};
  const { amount: monthlyAmount } = monthly || {};

  const savings = ((monthlyAmount! * 12 - yearlyAmount!) / (monthlyAmount! * 12)) * 100;
  return `${roundToNearestTen(savings)}%`;
};

/*
  Get online plan details and get the appropriate
  benefits and format it to look like smart home
*/

export const formatOnlinePlanForReducer = ({ planProducts }: FilterPlanProductsProps) => {
  const { homeTech, online } = ONLINE_DETAILS;
  const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const plan = planProducts.find((product: { id: number }) => product.id === ONLINE_PLAN_PRODUCT_ID) || ({} as TClientPlans);
  const hellotechHomePlans = plan?.prices.reduce(
    (accu: any, current: { planInterval: string }) => {
      const { planInterval } = current;
      return { ...accu, [planInterval]: current };
    },
    { [ANNUAL]: null, [MONTHLY]: null },
  );
  const benefits = [homeTech, online];
  let formatted = {};
  [ANNUAL, MONTHLY].forEach(cycle => {
    formatted = {
      ...formatted,
      [cycle]: {
        ...hellotechHomePlans[cycle],
        benefits,
      },
    };
  });

  return { ...formatted } as TPlansInfo;
};
export const formatHTLitePlanForReducer = ({ planProducts }: { planProducts: TClientPlans[] }) => {
  const { ANNUAL, MONTHLY } = UPSELL_PLAN_BILLING_CYCLES;
  const plan = planProducts.find(product => product.id === HELLOTECH_LITE_PLAN_PRODUCT_ID);

  const htLiteMonthlyPlans = {
    [ANNUAL]: null,
    [MONTHLY]: null,
  } as TPlansInfo;

  (plan?.prices || []).forEach(pricePlan => {
    const { id } = pricePlan;
    if (id === HELLOTECH_LITE_PLAN) {
      htLiteMonthlyPlans[MONTHLY] = {
        benefits: [HELLOTECH_LITE_DETAILS.online],
        ...pricePlan,
      };
    } else if (id === HELLOTECH_LITE_PLAN_ANNUAL_ID) {
      htLiteMonthlyPlans[ANNUAL] = {
        benefits: [HELLOTECH_LITE_DETAILS.online],
        ...pricePlan,
      };
    }
  });

  return htLiteMonthlyPlans;
};
